import React from 'react';
import {
  AreaChart as RechartsAreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { cn } from '../../lib/utils';

interface AreaChartProps {
  /** Chart data */
  data: any[];
  /** Chart title */
  title?: string;
  /** Chart height in pixels */
  height?: number;
  /** Custom className */
  className?: string;
  /** X-axis data key */
  xAxisKey?: string;
  /** Area data keys */
  areas?: Array<{
    dataKey: string;
    fill: string;
    stroke: string;
    name: string;
  }>;
}

/**
 * AreaChart wrapper component for cumulative metrics
 *
 * @example
 * ```tsx
 * <AreaChart
 *   data={cumulativeStats}
 *   title="Cumulative Traffic"
 *   xAxisKey="date"
 *   areas={[
 *     { dataKey: 'total', fill: '#3b82f6', stroke: '#2563eb', name: 'Total Requests' },
 *     { dataKey: 'human', fill: '#10b981', stroke: '#059669', name: 'Human' }
 *   ]}
 * />
 * ```
 */
export const AreaChart: React.FC<AreaChartProps> = ({
  data,
  title,
  height = 300,
  className,
  xAxisKey = 'date',
  areas = [
    {
      dataKey: 'human',
      fill: '#10b981',
      stroke: '#059669',
      name: 'Human',
    },
    {
      dataKey: 'bot',
      fill: '#ef4444',
      stroke: '#dc2626',
      name: 'Bot',
    },
  ],
}) => {
  // Show empty state if no data
  if (!data || data.length === 0) {
    return (
      <div
        className={cn(
          'flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200',
          className
        )}
        style={{ height }}
      >
        <p className="text-gray-500">No data available</p>
      </div>
    );
  }

  return (
    <div className={cn('w-full', className)}>
      {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}
      <ResponsiveContainer width="100%" height={height}>
        <RechartsAreaChart
          data={data}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={xAxisKey} />
          <YAxis />
          <Tooltip />
          <Legend />
          {areas.map((area) => (
            <Area
              key={area.dataKey}
              type="monotone"
              dataKey={area.dataKey}
              fill={area.fill}
              stroke={area.stroke}
              name={area.name}
              fillOpacity={0.6}
            />
          ))}
        </RechartsAreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default AreaChart;
